<template>
  <div>
    <div class="d-flex justify-content-end mb-1">
      <recalculate-summary-button
        v-if="$store.state.app.isMaster($store)"
        :selected-month="selMonthString"
        @reload="reload"
      />
    </div>
    <b-card body-class="p-2 p-md-3">
      <b-row class="m-0 pr-md-1">
        <b-col
          cols="12"
          md="6"
          class="mb-2 mb-md-0"
        >
          <b-form-group
            :label="$t('เดือน')"
            label-cols="12"
            label-cols-md="2"
            label-align-md="center"
            label-class="mb-1 mb-md-0"
            class="mb-0"
          >
            <flat-pickr
              v-model="selMonth"
              class="form-control"
              :config="config"
              @on-change="selectMonth"
            />
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          md="6"
          class="mb-2 mb-md-0"
        >
          <b-form-group
            :label="$t('ตัวแทน')"
            label-cols="12"
            label-cols-md="2"
            label-align-md="center"
            label-class="mb-1 mb-md-0"
            class="mb-0"
          >
            <agent-selector
              v-model="selectedAgent"
              @change="selectAgent"
            />
          </b-form-group>
        </b-col>
      </b-row>
    </b-card>

    <b-card>
      <div class="position-relative">
        <div
          v-if="loading"
          class="custom-overlay"
        >
          <div class="overlay-spinner">
            <b-spinner variant="primary" />
            <p class="mt-2 mb-0">
              <strong>{{ $t('กำลังโหลดข้อมูล...') }}</strong>
            </p>
          </div>
        </div>
        <b-table
          responsive
          bordered
          :fields="$tFields(columns)"
          :items="displayData"
          :class="{ 'table-loading': loading }"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BFormGroup, BTable, BRow, BCol, BSpinner,
} from 'bootstrap-vue'
import lottoService from '@/service/lottoService'
import flatPickr from 'vue-flatpickr-component'
import monthSelectPlugin from 'flatpickr/dist/plugins/monthSelect/index'
import RecalculateSummaryButton from '@core/components/recalculate/RecalculateSummaryButton.vue'
import AgentSelector from '@core/components/agent/AgentSelector.vue'
import 'flatpickr/dist/plugins/monthSelect/style.css'

const date = new Date()

export default {
  name: 'SummaryTable',
  components: {
    BTable,
    BFormGroup,
    BCard,
    BRow,
    BCol,
    BSpinner,
    flatPickr,
    RecalculateSummaryButton,
    AgentSelector,
  },
  props: {
    dataField: {
      type: String,
      required: true,
      validator: value => ['bet', 'pay', 'income', 'poy'].includes(value),
    },
    formatType: {
      type: String,
      required: true,
      validator: value => ['number', 'poy'].includes(value),
    },
  },
  data() {
    return {
      config: {
        plugins: [
          // eslint-disable-next-line new-cap
          new monthSelectPlugin({
            shorthand: true,
            dateFormat: 'm/Y',
          }),
        ],
      },
      selMonth: this.ParseDate(
        new Date(date.getFullYear(), date.getMonth(), 1),
      ),
      selMonthString: this.ParseDate(
        new Date(date.getFullYear(), date.getMonth(), 1),
      ),
      selectedAgent: null,
      loading: false,
      columns: [
        {
          key: 'date',
          label: 'วันที่',
          class: 'text-nowrap',
        },
        {
          key: 'all',
          label: 'ทั้งหมด',
          class: 'text-nowrap',
        },
      ],
      data: [],
      dictLoaded: false,
      initialLoad: true,
    }
  },
  computed: {
    displayData() {
      // If loading and no data, show placeholder with current month dates
      if (this.loading && this.data.length === 0) {
        return this.generatePlaceholderData()
      }
      return this.data
    },
  },
  async mounted() {
    await this.loadHuayDict()
    // Don't reload here - wait for AgentSelector to emit first
    // This prevents duplicate API calls on initial load
  },
  methods: {
    generatePlaceholderData() {
      // Parse selected month to get year and month
      const [month, year] = this.selMonthString.split('/')
      const daysInMonth = new Date(parseInt(year, 10), parseInt(month, 10), 0).getDate()

      const placeholderData = []
      // Generate rows for each day of the month
      for (let day = 1; day <= daysInMonth; day += 1) {
        const dateStr = `${this.pad2(day)}/${this.pad2(parseInt(month, 10))}/${year}`
        const row = { date: dateStr }

        // Set all column values to 0 or appropriate placeholder
        this.columns.forEach(col => {
          if (col.key !== 'date') {
            row[col.key] = this.formatType === 'poy' ? '0/0' : '0.00'
          }
        })

        placeholderData.push(row)
      }

      // Add summary row
      const summaryRow = { date: 'รวม' }
      this.columns.forEach(col => {
        if (col.key !== 'date') {
          summaryRow[col.key] = this.formatType === 'poy' ? '0/0' : '0.00'
        }
      })
      placeholderData.push(summaryRow)

      return placeholderData
    },
    async loadHuayDict() {
      if (this.dictLoaded) return

      try {
        const res = await lottoService.getAuthen('huay/dict')
        if (res.status === 200) {
          const data = [{ id: '0', name: 'หวยยี่กี' }, ...res.data]
          // eslint-disable-next-line no-restricted-syntax
          for (const v of data) {
            this.columns.push({
              label: v.name,
              key: v.id,
              class: 'text-nowrap',
            })
          }
          this.dictLoaded = true
        }
      } catch (error) {
        // Error handling without console
      }
    },
    selectAgent() {
      // Always reload when agent changes
      // On initial load, AgentSelector will emit:
      //   - null if first agent is current user (no duplicate call)
      //   - agent object if first agent is different (load with agent param)
      if (this.initialLoad) {
        this.initialLoad = false
      }
      this.reload()
    },
    pad2(number) {
      return (number < 10 ? '0' : '') + number
    },
    ParseDate(d) {
      return `${this.pad2(d.getMonth() + 1)}/${d.getFullYear()}`
    },
    selectMonth(e) {
      const d = this.ParseDate(
        new Date(e[0].getFullYear(), e[0].getMonth(), 1),
      )
      this.selMonthString = d
      this.reload()
    },
    formatValue(itemData) {
      if (this.formatType === 'number') {
        const value = itemData[this.dataField]
        return this.$store.state.app.toNumber(value.toFixed(2))
      }
      if (this.formatType === 'poy') {
        return `${itemData.poy}/${itemData.poyPerUser}`
      }
      return itemData[this.dataField]
    },
    async reload() {
      this.loading = true

      const agentParam = this.selectedAgent ? `&agent=${this.selectedAgent.code}` : ''
      const res = await lottoService.getAuthen(`summary/month/?month=${this.selMonthString}${agentParam}`)

      const newData = []
      if (res.status === 200) {
        const resData = res.data.data
        // eslint-disable-next-line no-restricted-syntax
        for (const dateKey in resData) {
          // eslint-disable-next-line no-prototype-builtins
          if (resData.hasOwnProperty(dateKey)) {
            const d = {}
            // eslint-disable-next-line no-restricted-syntax
            for (const key in resData[dateKey]) {
              // eslint-disable-next-line no-prototype-builtins
              if (resData[dateKey].hasOwnProperty(key)) {
                d[key] = this.formatValue(resData[dateKey][key])
              }
            }
            newData.push({
              date: dateKey,
              ...d,
            })
          }
        }

        const { summary } = res.data
        const d = {}
        // eslint-disable-next-line no-restricted-syntax
        for (const key in summary) {
          // eslint-disable-next-line no-prototype-builtins
          if (summary.hasOwnProperty(key)) {
            d[key] = this.formatValue(summary[key])
          }
        }
        newData.push({
          date: 'รวม',
          ...d,
        })
      }

      this.data = newData
      this.loading = false
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-flatpicker.scss';

.custom-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.6);
  z-index: 10;
  border-radius: 0.357rem;
}

.overlay-spinner {
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  text-align: center;
}

.table-loading {
  opacity: 0.5;
  pointer-events: none;
}
</style>
